import { Box as LayoutBox } from '~/components/Layout/Box'
import TradeForm from '~/components/TradeForm'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import IconSwapArrow from '~/assets/images/swap-arrow.svg'
import { updateReceipt, useWETHContext } from '../../context'
import {
  useGA,
  useWETHDepositCallback,
  useWETHWithdrawCallback,
  useBreakpoint,
  useWETHRouter,
  useSpotSwapRouter,
} from '~/hooks'
import { useCallback, useState } from 'react'
import { message } from '~/components/Message'
import { ethers } from 'ethers'
import { useInputAmountVerify } from '~/state/dex/hooks'
import { useTranslation } from 'react-i18next'
import { StyledBalanceWarning, StyledReviewBox } from './styled'
import BigNumber from 'bignumber.js'
import { MaxUint256 } from '@ethersproject/constants'
import { handleTxError } from '~/utils/handleTxError'
import { useWeb3React } from '@web3-react/core'
import { formatFormAmount } from '~/utils/numbers'

export const Review = () => {
  const { isMobile } = useBreakpoint()
  const { state, dispatch } = useWETHContext()
  const { trackClick } = useGA()
  const isWETHRouter = useWETHRouter()
  const isSpotSwapRouter = useSpotSwapRouter()
  const { account } = useWeb3React()
  const { gasInfo, value, isWrap, inputToken, outputToken, balance } = state
  const { t } = useTranslation()
  const title = t('order_preview')
  const isOpen = useModalOpened(ApplicationModal.WETH_PREVIEW)
  const toggleResultModal = useModalToggle(ApplicationModal.WETH_RESULT)
  const toggleWrapModal = useModalToggle(ApplicationModal.WETH_WRAP)
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    stack: true,
  })
  const { deposit, estimateGas: depositEstimate } = useWETHDepositCallback()
  const { withdraw, estimateGas: withdrawEstimate } = useWETHWithdrawCallback()
  const inputVerify = useInputAmountVerify()
  const [submitting, setSubmitting] = useState(false)
  const balanceFormatted = balance?.toAutoFormat() || MaxUint256.toString()
  const showWarning =
    new BigNumber(balanceFormatted)
      .minus(value)
      .div(balanceFormatted)
      .lte(0.02) && inputToken?.symbol === 'ETH'

  const verify = () => {
    if (!account) {
      trackClick('webtl_walletconnect', {
        from: isWrap ? 'wrap_weth' : 'unwrap_weth',
      })
      toggleWalletModal()
      return false
    }
    if (!inputVerify(value)) {
      message.error(t('unvalid_input_amount'))
      return false
    }

    if (balance.lt(ethers.utils.parseEther(value).toString())) {
      message.error(
        t('$symbol_insufficient_balance', { symbol: inputToken.symbol }),
      )
      return false
    }
    return true
  }

  const onConfirm = useCallback(async () => {
    if (!verify()) return

    setSubmitting(true)
    let estimateGas
    try {
      const v = ethers.utils.parseEther(value)
      estimateGas = isWrap
        ? await depositEstimate(v)
        : await withdrawEstimate(v)
    } catch (err) {
      console.error('estimate error', err)
    }
    try {
      const v = ethers.utils.parseEther(value).toString()
      const tx = isWrap
        ? await deposit(v, estimateGas)
        : await withdraw(v, estimateGas)
      console.log({ tx })
      const receipt = await tx.wait()
      console.log({ receipt })
      setSubmitting(false)
      dispatch(updateReceipt(receipt))
      trackClick('webtl_weth_preview_confirm', {
        taker_token_symbol: outputToken.symbol,
        maker_token_symbol: inputToken.symbol,
        taker_token_amount: value,
        maker_token_amount: value,
        is_success: 1,
      })
    } catch (error) {
      console.error(error)
      setSubmitting(false)
      trackClick('webtl_weth_preview_confirm', {
        taker_token_symbol: outputToken.symbol,
        maker_token_symbol: inputToken.symbol,
        taker_token_amount: value,
        maker_token_amount: value,
        is_success: 0,
        message: error?.message,
      })
      // user reject
      if (error && Number(error.code) === 4001) {
        message.error(t('sign_order_failure'))
        return
      }
      message.error(error.message)
      handleTxError(error)
    }
    toggleResultModal()
  }, [toggleResultModal, depositEstimate, withdrawEstimate, deposit, withdraw])
  const onCancel = () => {
    setSubmitting(false)
    toggleWrapModal()
  }

  const needPadding = isWETHRouter || (isMobile && !isSpotSwapRouter)

  return (
    <StyledReviewBox
      $pad={needPadding}
      visible={isOpen}
      title={title}
      onBack={onCancel}
      nest
    >
      <TradeForm.Body>
        <TradeForm.TokenRow token={inputToken} amount={value} />
        <LayoutBox ml="2px">
          <img src={IconSwapArrow} />
        </LayoutBox>
        <TradeForm.TokenRow token={outputToken} amount={value} />
        <LayoutBox mt="-8px">
          <TradeForm.Divider dashed />
        </LayoutBox>
        <TradeForm.Stack>
          <TradeForm.TipRow title={t('rate')}>
            1 {inputToken?.symbol} = 1 {outputToken?.symbol}
          </TradeForm.TipRow>
          <TradeForm.TipRow title={t('gas_fee')}>
            ${gasInfo.useValue}({formatFormAmount(gasInfo.ether, 3)} ETH)
          </TradeForm.TipRow>
          <TradeForm.TipRow title={t('trading_fee')}>$0(0)</TradeForm.TipRow>
          <TradeForm.TipRow title={t('network')}>
            <TradeForm.Network />
          </TradeForm.TipRow>
        </TradeForm.Stack>
        <TradeForm.Spacer h={60} />
        {showWarning && (
          <StyledBalanceWarning>
            {t('gas_reserve_warning')}
          </StyledBalanceWarning>
        )}
        <TradeForm.Button
          disabled={submitting || !account}
          loading={submitting}
          onClick={onConfirm}
        >
          {submitting ? t('confirming') : t('confirm')}
        </TradeForm.Button>
        {submitting && (
          <TradeForm.FootNote>{t('confirming_notice')}</TradeForm.FootNote>
        )}
      </TradeForm.Body>
    </StyledReviewBox>
  )
}
